import { UseQueryResult } from 'react-query';
import { BankDealProfile, ContactDetailUser } from '../models';
import React from 'react';
import { CerebroHeading } from './CerebroHeading';
import { CerebroText } from './CerebroText';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Grid, Skeleton, Tab } from '@mui/material';
import NumberFormat from 'react-number-format';
import { CerebroLabel } from './CerebroLabel';
import { hasExclusions, hasIntangibles, hasPrograms, hasSpecialties, hasTangibles } from '../utils/contactProfile';
import { PlatformRootUrl } from '../utils/platform';
import { CerebroLink } from './CerebroLink';

interface BankDealProfileLayoutProps {
	dealProfileQuery: UseQueryResult<BankDealProfile, unknown>;
}

export const BankDealProfileLayout: React.FC<BankDealProfileLayoutProps> = ({ dealProfileQuery }) => {
	const [tab, setTab] = React.useState('criteria');
	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};

	const displayNotes = (headingText: string, notes: string): JSX.Element => {
		if (notes) {
			const paragraphs = notes.split(/\r?\n/);
			return (
				<>
					<CerebroHeading size="h3" component="h3">
						{headingText}
					</CerebroHeading>
					{paragraphs.map(p => (
						<CerebroText sx={{ marginTop: 1, marginBottom: 1 }} key={p}>
							{p}
						</CerebroText>
					))}
					<CerebroText>&nbsp;</CerebroText>
				</>
			);
		}

		return <></>;
	};

	return (
		<TabContext value={tab}>
			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<TabList onChange={handleTabChange}>
					<Tab label="Deal Criteria" value="criteria" />
					<Tab label="Notes" value="notes" />
				</TabList>
			</Box>

			<TabPanel value="criteria">
				<Grid container spacing={3}>
					{dealProfileQuery.isSuccess && (
						<>
							<Grid item xs={8}>
								<CerebroLabel>Debt &#38; Equity Financing Types</CerebroLabel>
								<CerebroText>{dealProfileQuery.data?.tier3ContactTypeDescription}</CerebroText>
							</Grid>
							<Grid item xs={8}>
								<CerebroLink
									target="_blank"
									href={`${PlatformRootUrl}/BankDealProfiles/UnderwritingCriteria/${dealProfileQuery.data?.bankContactGuid}`}
								>
									Deal Profile Summary
								</CerebroLink>
								<CerebroText>
									Target EBITDA -{' '}
									<NumberFormat
										value={dealProfileQuery.data?.targetEbitdaMin}
										displayType="text"
										thousandSeparator={true}
										prefix="$"
									/>{' '}
									{dealProfileQuery.data?.targetEbitdaMax ? (
										<>
											- {''}
											<NumberFormat
												value={dealProfileQuery.data?.targetEbitdaMax}
												displayType="text"
												thousandSeparator={true}
												prefix="$"
											/>
										</>
									) : (
										<>+</>
									)}
								</CerebroText>
								<CerebroText>
									Target Revenue -{' '}
									<NumberFormat
										value={dealProfileQuery.data?.targetRevenueMin}
										displayType="text"
										thousandSeparator={true}
										prefix="$"
									/>{' '}
									{dealProfileQuery.data?.targetRevenueMax ? (
										<>
											{' '}
											- {''}
											<NumberFormat
												value={dealProfileQuery.data?.targetRevenueMax}
												displayType="text"
												thousandSeparator={true}
												prefix="$"
											/>
										</>
									) : (
										<>+</>
									)}
								</CerebroText>
								<CerebroText>
									Target Deal Size -{' '}
									<NumberFormat
										value={dealProfileQuery.data?.targetDealSizeMin}
										displayType="text"
										thousandSeparator={true}
										prefix="$"
									/>{' '}
									{dealProfileQuery.data?.targetDealSizeMax ? (
										<>
											- {''}
											<NumberFormat
												value={dealProfileQuery.data?.targetDealSizeMax}
												displayType="text"
												thousandSeparator={true}
												prefix="$"
											/>
										</>
									) : (
										<>+</>
									)}
								</CerebroText>
							</Grid>
						</>
					)}

					{dealProfileQuery.isSuccess && (
						<Grid item xs={8}>
							<CerebroLabel>Geography</CerebroLabel>

							{dealProfileQuery.data.allStates && <CerebroText>Cover Entire Nation - Yes</CerebroText>}

							{!dealProfileQuery.data.allStates && (
								<>
									{dealProfileQuery.data.targetStates?.map(s => {
										return <CerebroText key={s.name}>{s.name}</CerebroText>;
									})}
								</>
							)}
						</Grid>
					)}

					{dealProfileQuery.isSuccess && (
						<Grid item xs={8}>
							<CerebroLabel>Industries (Excluded & Specialties)</CerebroLabel>

							{dealProfileQuery.data.criteriaLendingStyle == 1 && (
								<>
									<CerebroText>Generalist</CerebroText>
									{hasExclusions(dealProfileQuery.data.targetIndustries) && (
										<>
											<br />
											<CerebroText variant="hint">(Exclusions)</CerebroText>
											{dealProfileQuery.data.targetIndustries
												?.filter(a => !a.isSpecialty)
												.map(i => {
													return <CerebroText key={i.guid}>{i.name}</CerebroText>;
												})}
										</>
									)}
								</>
							)}

							{dealProfileQuery.data.criteriaLendingStyle == 2 && (
								<>
									<CerebroText>Specialist</CerebroText>
									{hasSpecialties(dealProfileQuery.data.targetIndustries) && (
										<>
											<br />
											<CerebroText variant="hint">(Specialties)</CerebroText>
											{dealProfileQuery.data.targetIndustries
												?.filter(a => a.isSpecialty)
												.map(i => {
													return <CerebroText key={i.guid}>{i.name}</CerebroText>;
												})}
										</>
									)}
								</>
							)}
						</Grid>
					)}

					{dealProfileQuery.isSuccess && dealProfileQuery.data.doesSeniorCashFlowDeals && (
						<Grid item xs={8}>
							<CerebroLabel>Senior Cash Flow Deals</CerebroLabel>
							<CerebroText>
								Target Leverage Ratio - {dealProfileQuery.data.seniorTargetLeverageRatio}
							</CerebroText>
						</Grid>
					)}

					{dealProfileQuery.isSuccess && dealProfileQuery.data.doesJuniorCashFlowDeals && (
						<Grid item xs={8}>
							<CerebroLabel>Junior Cash Flow Deals</CerebroLabel>
							<CerebroText>
								Target Leverage Ratio - {dealProfileQuery.data.juniorTargetLeverageRatio}
							</CerebroText>
						</Grid>
					)}

					{dealProfileQuery.isSuccess && (
						<>
							<Grid item xs={8}>
								<CerebroLabel>Working Capital</CerebroLabel>
								{dealProfileQuery.data.assetReceivables && (
									<>
										<CerebroText>
											Receivables -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.accountsReceivableAdvanceRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetInventory && (
									<>
										<CerebroText>
											Inventory -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.advanceRateInventory}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.doesAssetBasedDeals && (
									<CerebroText>
										Inventory Only - {dealProfileQuery.data.doesInventoryOnly ? 'Yes' : 'No'}
									</CerebroText>
								)}
							</Grid>
						</>
					)}

					{dealProfileQuery.isSuccess && hasTangibles(dealProfileQuery.data) && (
						<>
							<Grid item xs={8}>
								<CerebroLabel>Tangibles / M&E Asset Types</CerebroLabel>

								{dealProfileQuery.data.assetEquipment && (
									<>
										<CerebroText>
											New Machinery & Equipment -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.advanceRateNewHeavyEquipment}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetUsedEquipment && (
									<>
										<CerebroText>
											Used Machinery & Equipment -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.advanceRateUsedHeavyEquipment}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetFleetVehicles && (
									<>
										<CerebroText>
											New Rolling Stock / Vehicles -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.assetFleetVehicleAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetUsedFleetVehicles && (
									<>
										<CerebroText>
											Used Rolling Stock / Vehicles -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.assetUsedFleetVehicleAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetMarineVessels && (
									<>
										<CerebroText>
											New Marine Vessels -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.assetMarineVesselAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetUsedMarineVessels && (
									<>
										<CerebroText>
											Used Marine Vessels -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.assetUsedMarineVesselAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetHelicopters && (
									<>
										<CerebroText>
											New Helicopters -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.assetHelicopterAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.usedAssetHelicopters && (
									<>
										<CerebroText>
											Used Helicopters -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.assetUsedHelicopterAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetFixedWingAircraft && (
									<>
										<CerebroText>
											New Fixed Wing Aircraft -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.assetFixedWingAircraftAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetUsedFixedWingAircraft && (
									<>
										<CerebroText>
											Used Fixed Wing Aircraft -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.assetUsedFixedWingAircraftAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}
							</Grid>
						</>
					)}

					{dealProfileQuery.isSuccess && hasIntangibles(dealProfileQuery.data) && (
						<>
							<Grid item xs={8}>
								<CerebroLabel>Intangible Asset Types</CerebroLabel>

								{dealProfileQuery.data.assetIntellectualProperty && (
									<>
										<CerebroText>
											Intellectual Property -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.assetIntellectualPropertyAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}

								{dealProfileQuery.data.assetRoyalty && (
									<>
										<CerebroText>
											Intellectual Property -{' '}
											<NumberFormat
												value={dealProfileQuery.data?.royaltyAdvancedRate}
												displayType="text"
											/>
											%
										</CerebroText>
									</>
								)}
							</Grid>
						</>
					)}

					{dealProfileQuery.isSuccess && hasPrograms(dealProfileQuery.data) && (
						<>
							<Grid item xs={8}>
								<CerebroLabel>Provided Programs</CerebroLabel>

								{dealProfileQuery.data.sba7aProgram && (
									<CerebroText>
										SBA 7a Program - {dealProfileQuery.data.sba7aProgram ? 'Yes' : 'No'}
									</CerebroText>
								)}

								{dealProfileQuery.data.sba504Program && (
									<CerebroText>
										SBA 504 Program - {dealProfileQuery.data.sba504Program ? 'Yes' : 'No'}
									</CerebroText>
								)}

								{dealProfileQuery.data.usdaBusinessProgram && (
									<CerebroText>
										USDA Business & Industry Program -{' '}
										{dealProfileQuery.data.usdaBusinessProgram ? 'Yes' : 'No'}
									</CerebroText>
								)}

								{dealProfileQuery.data.usdaRuralEnergyProgram && (
									<CerebroText>
										USDA Rural Energy for America Program -{' '}
										{dealProfileQuery.data.usdaRuralEnergyProgram ? 'Yes' : 'No'}
									</CerebroText>
								)}

								{dealProfileQuery.data.usdaCommunityProgram && (
									<CerebroText>
										USDA Community Facilities Program -{' '}
										{dealProfileQuery.data.usdaCommunityProgram ? 'Yes' : 'No'}
									</CerebroText>
								)}

								{dealProfileQuery.data.usda9003Program && (
									<CerebroText>
										USDA 9003 Program - {dealProfileQuery.data.usda9003Program ? 'Yes' : 'No'}
									</CerebroText>
								)}
							</Grid>
						</>
					)}
				</Grid>
			</TabPanel>

			<TabPanel value="notes">
				{dealProfileQuery.isSuccess && (
					<>
						{displayNotes('Bank', dealProfileQuery.data.bankNotes)}
						{displayNotes('General', dealProfileQuery.data.generalSectionNotes)}
						{displayNotes('Senior Cash', dealProfileQuery.data.seniorCashSectionNotes)}
						{displayNotes('Junior Cash', dealProfileQuery.data.juniorCashSectionNotes)}
						{displayNotes('Asset-Based', dealProfileQuery.data.assetBasedSectionNotes)}
						{displayNotes('Equity Investing', dealProfileQuery.data.equitySectionNotes)}
						{displayNotes('Venture Debt / Recurring Revenue', dealProfileQuery.data.ventureSectionNotes)}
						{displayNotes('Senior R/E', dealProfileQuery.data.seniorRealEstateSectionNotes)}
						{displayNotes('Junior/Mezz R/E', dealProfileQuery.data.juniorRealEstateSectionNotes)}
						{displayNotes('SBA', dealProfileQuery.data.sbaSectionNotes)}
					</>
				)}

				{dealProfileQuery.isLoading && (
					<>
						<Skeleton height={90} />
						<Skeleton height={90} />
						<Skeleton height={90} />
					</>
				)}
			</TabPanel>
		</TabContext>
	);
};
